import { z } from 'zod'
import { asEnumType } from '@gqloom/zod'

export const PolicyEquipmentScope = {
  UV: 'UV',
  RED_LIGHT: 'RED_LIGHT',
  SPRAY: 'SPRAY',
  SPA: 'SPA',
  WELLNESS: 'WELLNESS',
  UNCATEGORISED: 'UNCATEGORISED',
} as const
export type PolicyEquipmentScope = (typeof PolicyEquipmentScope)[keyof typeof PolicyEquipmentScope]
export const policyEquipmentScopeSchema = z.enum(PolicyEquipmentScope).register(asEnumType, {
  name: 'PolicyEquipmentScope',
  description: 'Equipment category a policy or jurisdiction rule covers; an empty scope means all',
})

export const PolicyAcceptanceMethod = {
  CHECKBOX: 'CHECKBOX',
  SIGNATURE: 'SIGNATURE',
} as const
export type PolicyAcceptanceMethod =
  (typeof PolicyAcceptanceMethod)[keyof typeof PolicyAcceptanceMethod]
export const policyAcceptanceMethodSchema = z.enum(PolicyAcceptanceMethod).register(asEnumType, {
  name: 'PolicyAcceptanceMethod',
  description: 'How a client accepts a site policy revision',
})

export const PolicySignerRule = {
  CLIENT: 'CLIENT',
  GUARDIAN_IF_MINOR: 'GUARDIAN_IF_MINOR',
  BOTH_IF_MINOR: 'BOTH_IF_MINOR',
} as const
export type PolicySignerRule = (typeof PolicySignerRule)[keyof typeof PolicySignerRule]
export const policySignerRuleSchema = z.enum(PolicySignerRule).register(asEnumType, {
  name: 'PolicySignerRule',
  description: 'Who must sign a policy when the participant is a minor',
})

export const SignerCapacity = {
  CLIENT_ADULT: 'CLIENT_ADULT',
  PARTICIPANT_MINOR: 'PARTICIPANT_MINOR',
  GUARDIAN: 'GUARDIAN',
} as const
export type SignerCapacity = (typeof SignerCapacity)[keyof typeof SignerCapacity]
export const signerCapacitySchema = z.enum(SignerCapacity).register(asEnumType, {
  name: 'SignerCapacity',
  description: 'The capacity a signer accepts a policy slot in',
})

export const PolicyRevisionStatus = {
  ACTIVE: 'ACTIVE',
  SCHEDULED: 'SCHEDULED',
  SUPERSEDED: 'SUPERSEDED',
  CANCELLED: 'CANCELLED',
  HISTORICAL: 'HISTORICAL',
} as const
export type PolicyRevisionStatus = (typeof PolicyRevisionStatus)[keyof typeof PolicyRevisionStatus]
export const policyRevisionStatusSchema = z.enum(PolicyRevisionStatus).register(asEnumType, {
  name: 'PolicyRevisionStatus',
  description: 'Lifecycle of an immutable site policy revision',
})

export const PolicyAcceptanceState = {
  COUNTED: 'COUNTED',
  DUPLICATE: 'DUPLICATE',
  VOIDED: 'VOIDED',
  CAPACITY_MISMATCH: 'CAPACITY_MISMATCH',
  UNVERIFIED_HISTORICAL: 'UNVERIFIED_HISTORICAL',
  WITHDRAWN: 'WITHDRAWN',
  REVOKED: 'REVOKED',
} as const
export type PolicyAcceptanceState =
  (typeof PolicyAcceptanceState)[keyof typeof PolicyAcceptanceState]
export const policyAcceptanceStateSchema = z.enum(PolicyAcceptanceState).register(asEnumType, {
  name: 'PolicyAcceptanceState',
  description: 'Whether a policy acceptance counts towards readiness, and why not',
})

export const EvidenceStatus = {
  PENDING: 'PENDING',
  READY: 'READY',
  FAILED: 'FAILED',
} as const
export type EvidenceStatus = (typeof EvidenceStatus)[keyof typeof EvidenceStatus]
export const evidenceStatusSchema = z.enum(EvidenceStatus).register(asEnumType, {
  name: 'PolicyEvidenceStatus',
  description: 'State of the signed PDF and certificate for an acceptance',
})

export const CopyStatus = {
  NOT_REQUESTED: 'NOT_REQUESTED',
  QUEUED: 'QUEUED',
  SENT: 'SENT',
  FAILED: 'FAILED',
  BOUNCED: 'BOUNCED',
} as const
export type CopyStatus = (typeof CopyStatus)[keyof typeof CopyStatus]
export const copyStatusSchema = z.enum(CopyStatus).register(asEnumType, {
  name: 'PolicyCopyStatus',
  description: 'Delivery state of the emailed copy of a signed document',
})

export const ImportProvenance = {
  SCANNED: 'SCANNED',
  TRUSTED_SOURCE: 'TRUSTED_SOURCE',
  NONE: 'NONE',
} as const
export type ImportProvenance = (typeof ImportProvenance)[keyof typeof ImportProvenance]
export const importProvenanceSchema = z.enum(ImportProvenance).register(asEnumType, {
  name: 'PolicyImportProvenance',
  description: 'What backs an imported policy acceptance',
})

export const SignatureMode = {
  DRAW: 'DRAW',
  TYPE: 'TYPE',
} as const
export type SignatureMode = (typeof SignatureMode)[keyof typeof SignatureMode]
export const signatureModeSchema = z.enum(SignatureMode).register(asEnumType, {
  name: 'PolicySignatureMode',
  description: 'How a policy signature was captured',
})

export const GuardianAssuranceLevel = {
  GUARDIAN_DECLARED: 'GUARDIAN_DECLARED',
  GUARDIAN_ID_CHECKED: 'GUARDIAN_ID_CHECKED',
} as const
export type GuardianAssuranceLevel =
  (typeof GuardianAssuranceLevel)[keyof typeof GuardianAssuranceLevel]
export const guardianAssuranceLevelSchema = z.enum(GuardianAssuranceLevel).register(asEnumType, {
  name: 'GuardianAssuranceLevel',
  description: 'How a guardian relationship was established',
})

export const AgeVerificationMethod = {
  SELF_REPORTED: 'SELF_REPORTED',
  STAFF_ID_CHECK: 'STAFF_ID_CHECK',
} as const
export type AgeVerificationMethod =
  (typeof AgeVerificationMethod)[keyof typeof AgeVerificationMethod]
export const ageVerificationMethodSchema = z.enum(AgeVerificationMethod).register(asEnumType, {
  name: 'AgeVerificationMethod',
  description: 'How a client date of birth was verified',
})

export const AgeEvidenceRequired = {
  SELF_REPORTED_OK: 'SELF_REPORTED_OK',
  STAFF_ID_CHECK: 'STAFF_ID_CHECK',
} as const
export type AgeEvidenceRequired = (typeof AgeEvidenceRequired)[keyof typeof AgeEvidenceRequired]
export const ageEvidenceRequiredSchema = z.enum(AgeEvidenceRequired).register(asEnumType, {
  name: 'AgeEvidenceRequired',
  description: 'The date of birth evidence a jurisdiction rule requires',
})

export const AgeBandOutcome = {
  ALLOWED: 'ALLOWED',
  PROHIBITED: 'PROHIBITED',
  GUARDIAN_CONSENT_SIGNED: 'GUARDIAN_CONSENT_SIGNED',
  GUARDIAN_PRESENT_EACH_VISIT: 'GUARDIAN_PRESENT_EACH_VISIT',
  MEDICAL_AUTHORIZATION: 'MEDICAL_AUTHORIZATION',
} as const
export type AgeBandOutcome = (typeof AgeBandOutcome)[keyof typeof AgeBandOutcome]
export const ageBandOutcomeSchema = z.enum(AgeBandOutcome).register(asEnumType, {
  name: 'AgeBandOutcome',
  description: 'What a jurisdiction rule requires of a client in an age band',
})

export const JurisdictionRuleStatus = {
  ACTIVE: 'ACTIVE',
  SUPERSEDED: 'SUPERSEDED',
  RETIRED: 'RETIRED',
} as const
export type JurisdictionRuleStatus =
  (typeof JurisdictionRuleStatus)[keyof typeof JurisdictionRuleStatus]
export const jurisdictionRuleStatusSchema = z.enum(JurisdictionRuleStatus).register(asEnumType, {
  name: 'JurisdictionRuleStatus',
  description: 'Lifecycle of a jurisdiction rule revision',
})

export const StartDecision = {
  ALLOW: 'ALLOW',
  DENY: 'DENY',
} as const
export type StartDecision = (typeof StartDecision)[keyof typeof StartDecision]
export const startDecisionSchema = z.enum(StartDecision).register(asEnumType, {
  name: 'EquipmentStartDecision',
  description: 'The authoritative outcome of an equipment start evaluation',
})

export const ReadinessDimension = {
  AGE: 'AGE',
  POLICY: 'POLICY',
  SKIN_TYPE: 'SKIN_TYPE',
  OPERATIONAL: 'OPERATIONAL',
} as const
export type ReadinessDimension = (typeof ReadinessDimension)[keyof typeof ReadinessDimension]
export const readinessDimensionSchema = z.enum(ReadinessDimension).register(asEnumType, {
  name: 'ReadinessDimension',
  description: 'One of the four independent equipment readiness dimensions',
})

export const ReadinessStatus = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const
export type ReadinessStatus = (typeof ReadinessStatus)[keyof typeof ReadinessStatus]
export const readinessStatusSchema = z.enum(ReadinessStatus).register(asEnumType, {
  name: 'ReadinessStatus',
  description: 'Result of one readiness dimension',
})

export const PolicyComplianceErrorCode = {
  POLICIES_NOT_ACCEPTED: 'POLICIES_NOT_ACCEPTED',
  AGE_VERIFICATION_REQUIRED: 'AGE_VERIFICATION_REQUIRED',
  AGE_PROHIBITED: 'AGE_PROHIBITED',
  MEDICAL_AUTHORIZATION_UNSUPPORTED: 'MEDICAL_AUTHORIZATION_UNSUPPORTED',
  GUARDIAN_PRESENCE_REQUIRED: 'GUARDIAN_PRESENCE_REQUIRED',
  ELIGIBILITY_UNAVAILABLE: 'ELIGIBILITY_UNAVAILABLE',
  POLICY_VERSION_CHANGED: 'POLICY_VERSION_CHANGED',
  IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',
  CAPACITY_MISMATCH: 'CAPACITY_MISMATCH',
} as const
export type PolicyComplianceErrorCode =
  (typeof PolicyComplianceErrorCode)[keyof typeof PolicyComplianceErrorCode]
export const policyComplianceErrorCodeSchema = z
  .enum(PolicyComplianceErrorCode)
  .register(asEnumType, {
    name: 'PolicyComplianceErrorCode',
    description: 'Reason codes the readiness gate and the pending-items writes refuse with',
  })

export const ReadinessReasonCode = {
  ...PolicyComplianceErrorCode,
  RENEWAL_EXPIRED: 'RENEWAL_EXPIRED',
  SKIN_TYPE_MISSING: 'SKIN_TYPE_MISSING',
  SKIN_TYPE_DUE: 'SKIN_TYPE_DUE',
  OPERATIONAL_BLOCKED: 'OPERATIONAL_BLOCKED',
} as const
export type ReadinessReasonCode = (typeof ReadinessReasonCode)[keyof typeof ReadinessReasonCode]
export const readinessReasonCodeSchema = z.enum(ReadinessReasonCode).register(asEnumType, {
  name: 'ReadinessReasonCode',
  description: 'Why a readiness dimension fails or an item is outstanding',
})

export const PendingItemKind = {
  SITE_POLICY: 'SITE_POLICY',
  SKIN_TYPE_FORM: 'SKIN_TYPE_FORM',
  PROFILE_FIELDS: 'PROFILE_FIELDS',
  SERVICE_AGREEMENT: 'SERVICE_AGREEMENT',
  SITE_FORM: 'SITE_FORM',
  AGE_VERIFICATION: 'AGE_VERIFICATION',
} as const
export type PendingItemKind = (typeof PendingItemKind)[keyof typeof PendingItemKind]
export const pendingItemKindSchema = z.enum(PendingItemKind).register(asEnumType, {
  name: 'PendingItemKind',
  description: 'The kind of obligation a pending item stands for',
})

export const ReadinessItemTiming = {
  DUE_NOW: 'DUE_NOW',
  RENEWAL: 'RENEWAL',
  SCHEDULED: 'SCHEDULED',
} as const
export type ReadinessItemTiming = (typeof ReadinessItemTiming)[keyof typeof ReadinessItemTiming]
export const readinessItemTimingSchema = z.enum(ReadinessItemTiming).register(asEnumType, {
  name: 'ReadinessItemTiming',
  description:
    'When an obligation falls due: now, as a renewal of a counted acceptance, or when a scheduled revision takes effect',
})

export const PendingItemsChannel = {
  SMS: 'SMS',
  QR: 'QR',
  DESK: 'DESK',
  APP: 'APP',
  KIOSK: 'KIOSK',
} as const
export type PendingItemsChannel = (typeof PendingItemsChannel)[keyof typeof PendingItemsChannel]
export const pendingItemsChannelSchema = z.enum(PendingItemsChannel).register(asEnumType, {
  name: 'PendingItemsChannel',
  description: 'How a pending-items handoff reaches its signer',
})

export const PendingItemsHandoffStatus = {
  CREATED: 'CREATED',
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  OPENED: 'OPENED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  SUPERSEDED: 'SUPERSEDED',
  LOCKED: 'LOCKED',
} as const
export type PendingItemsHandoffStatus =
  (typeof PendingItemsHandoffStatus)[keyof typeof PendingItemsHandoffStatus]
export const pendingItemsHandoffStatusSchema = z
  .enum(PendingItemsHandoffStatus)
  .register(asEnumType, {
    name: 'PendingItemsHandoffStatus',
    description: 'Lifecycle of a pending-items handoff',
  })

export const PendingItemHandoffCapability = {
  PENDING_ITEMS: 'PENDING_ITEMS',
  AGREEMENT_SIGN: 'AGREEMENT_SIGN',
  FORM_FILL: 'FORM_FILL',
} as const
export type PendingItemHandoffCapability =
  (typeof PendingItemHandoffCapability)[keyof typeof PendingItemHandoffCapability]
export const pendingItemHandoffCapabilitySchema = z
  .enum(PendingItemHandoffCapability)
  .register(asEnumType, {
    name: 'PendingItemHandoffCapability',
    description:
      'Which capability completes a pending item: this link, or the agreement or form link it hands off to',
  })

export const PendingItemsRequestSource = {
  STAFF: 'STAFF',
  AUTO_CHECKIN: 'AUTO_CHECKIN',
  BULK_REPORT: 'BULK_REPORT',
  RENEWAL_REMINDER: 'RENEWAL_REMINDER',
  FIRST_VISIT: 'FIRST_VISIT',
  POLICY_PUBLISH: 'POLICY_PUBLISH',
  CLIENT_APP: 'CLIENT_APP',
} as const
export type PendingItemsRequestSource =
  (typeof PendingItemsRequestSource)[keyof typeof PendingItemsRequestSource]
export const pendingItemsRequestSourceSchema = z
  .enum(PendingItemsRequestSource)
  .register(asEnumType, {
    name: 'PendingItemsRequestSource',
    description: 'What asked for a pending-items handoff',
  })

export const IdentityVerificationLevel = {
  LINK_POSSESSION: 'LINK_POSSESSION',
  DOB_MATCH: 'DOB_MATCH',
  STAFF_PRESENT: 'STAFF_PRESENT',
  CLIENT_SESSION: 'CLIENT_SESSION',
} as const
export type IdentityVerificationLevel =
  (typeof IdentityVerificationLevel)[keyof typeof IdentityVerificationLevel]
export const identityVerificationLevelSchema = z
  .enum(IdentityVerificationLevel)
  .register(asEnumType, {
    name: 'IdentityVerificationLevel',
    description: 'The identity assurance a handoff channel provides',
  })

export const OutboxKind = {
  SMS: 'SMS',
  PDF: 'PDF',
  COPY_EMAIL: 'COPY_EMAIL',
  AUDIT: 'AUDIT',
  SIGNAL: 'SIGNAL',
  PUSH: 'PUSH',
} as const
export type OutboxKind = (typeof OutboxKind)[keyof typeof OutboxKind]
export const outboxKindSchema = z.enum(OutboxKind)

export const OutboxStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
  DEAD: 'DEAD',
} as const
export type OutboxStatus = (typeof OutboxStatus)[keyof typeof OutboxStatus]
export const outboxStatusSchema = z.enum(OutboxStatus)

export const MessagingConsentPurpose = {
  COMPLIANCE_LINKS: 'COMPLIANCE_LINKS',
} as const
export type MessagingConsentPurpose =
  (typeof MessagingConsentPurpose)[keyof typeof MessagingConsentPurpose]
export const messagingConsentPurposeSchema = z.enum(MessagingConsentPurpose).register(asEnumType, {
  name: 'MessagingConsentPurpose',
  description: 'What a recipient consented to receive texts for',
})

export const MessagingConsentSource = {
  STAFF_ATTESTED: 'STAFF_ATTESTED',
  GUARDIAN_ATTESTED: 'GUARDIAN_ATTESTED',
  CLIENT_ATTESTED: 'CLIENT_ATTESTED',
  INBOUND_START: 'INBOUND_START',
} as const
export type MessagingConsentSource =
  (typeof MessagingConsentSource)[keyof typeof MessagingConsentSource]
export const messagingConsentSourceSchema = z.enum(MessagingConsentSource).register(asEnumType, {
  name: 'MessagingConsentSource',
  description: 'Who attested to a messaging consent',
})

export const MessagingSuppressionReason = {
  STOP: 'STOP',
  PROVIDER_OPT_OUT: 'PROVIDER_OPT_OUT',
  STAFF_RECORDED: 'STAFF_RECORDED',
} as const
export type MessagingSuppressionReason =
  (typeof MessagingSuppressionReason)[keyof typeof MessagingSuppressionReason]
export const messagingSuppressionReasonSchema = z.enum(MessagingSuppressionReason)

export const ConsentWithdrawalSource = {
  SIGNER: 'SIGNER',
  STAFF_ON_BEHALF: 'STAFF_ON_BEHALF',
  INBOUND_STOP: 'INBOUND_STOP',
} as const
export type ConsentWithdrawalSource =
  (typeof ConsentWithdrawalSource)[keyof typeof ConsentWithdrawalSource]
export const consentWithdrawalSourceSchema = z.enum(ConsentWithdrawalSource).register(asEnumType, {
  name: 'ConsentWithdrawalSource',
  description: 'Who recorded a consent or permission withdrawal',
})

export const ESignConsentProfile = {
  US_ACCESS_CODE: 'US_ACCESS_CODE',
  AU_LINK: 'AU_LINK',
} as const
export type ESignConsentProfile = (typeof ESignConsentProfile)[keyof typeof ESignConsentProfile]
export const eSignConsentProfileSchema = z.enum(ESignConsentProfile).register(asEnumType, {
  name: 'ESignConsentProfile',
  description: 'The electronic consent ceremony a site uses',
})

export const LegalHoldScope = {
  CLIENT: 'CLIENT',
  SITE: 'SITE',
  AGREEMENT: 'AGREEMENT',
} as const
export type LegalHoldScope = (typeof LegalHoldScope)[keyof typeof LegalHoldScope]
export const legalHoldScopeSchema = z.enum(LegalHoldScope)

export const PendingItemsErrorCode = {
  NOT_FOUND: 'NOT_FOUND',
  LINK_INACTIVE: 'LINK_INACTIVE',
  NOTHING_PENDING: 'NOTHING_PENDING',
  CHANNEL_NOT_ALLOWED: 'CHANNEL_NOT_ALLOWED',
  GUARDIAN_REQUIRED: 'GUARDIAN_REQUIRED',
  RECIPIENT_UNREACHABLE: 'RECIPIENT_UNREACHABLE',
  VERIFIED_DOB_REQUIRED: 'VERIFIED_DOB_REQUIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  IDENTITY_NOT_CONFIRMED: 'IDENTITY_NOT_CONFIRMED',
  IDENTITY_MISMATCH: 'IDENTITY_MISMATCH',
  IDENTITY_LOCKED: 'IDENTITY_LOCKED',
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  DISCLOSURE_CHANGED: 'DISCLOSURE_CHANGED',
  GUARDIAN_DECLARATION_INVALID: 'GUARDIAN_DECLARATION_INVALID',
  CAPABILITY_REFUSED: 'CAPABILITY_REFUSED',
  SIGNATURE_REQUIRED: 'SIGNATURE_REQUIRED',
  TYPED_NAME_MISMATCH: 'TYPED_NAME_MISMATCH',
  UPLOAD_INVALID: 'UPLOAD_INVALID',
  CSRF_REJECTED: 'CSRF_REJECTED',
  ACCESS_CODE_REQUIRED: 'ACCESS_CODE_REQUIRED',
  ACCESS_CODE_MISMATCH: 'ACCESS_CODE_MISMATCH',
  FEATURE_UNAVAILABLE: 'FEATURE_UNAVAILABLE',
  NOT_GUARDIAN: 'NOT_GUARDIAN',
  HANDOFF_MISMATCH: 'HANDOFF_MISMATCH',
  PRESENCE_REQUIRED: 'PRESENCE_REQUIRED',
} as const
export type PendingItemsErrorCode =
  (typeof PendingItemsErrorCode)[keyof typeof PendingItemsErrorCode]
export const pendingItemsErrorCodeSchema = z.enum(PendingItemsErrorCode).register(asEnumType, {
  name: 'PendingItemsErrorCode',
  description: 'Why a pending-items handoff, session or write was refused',
})
